import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import orderService from './orderService';

const initialState = {
    orders: [],
    omniva_locations: [],
    order: null,
    summary: null,
    isError: false,
    isSuccess: false,
    isLoading: false,
    isSummaryLoading: false,
    isLocationsLoading: false,
    message: '',
    summaryMessage: '',
    currentPage: 1,
    totalPages: 1,
    totalOrders: 0,
    pageLimit: 20,
};

// Initiate Stripe payment
export const initiateStripe = createAsyncThunk(
    'order/initiateStripe',
    async (orderData, thunkAPI) => {
        try {
            return await orderService.initiateStripe(orderData);
        } catch (error) {
            const message = (error.response?.data?.message) || error.message || error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Get order by session ID
export const getOrderBySessionId = createAsyncThunk(
    'order/getBySessionId',
    async (sessionId, thunkAPI) => {
        try {
            return await orderService.getOrderBySessionId(sessionId);
        } catch (error) {
            const message = (error.response?.data?.message) || error.message || error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

export const getAdminOrders = createAsyncThunk(
    'order/getAllOrders', 
    async ({ page = 1, limit = 20, includeUnpaidCancelled = false } = {}, thunkAPI) => {
        try {
            return await orderService.getAdminOrders({ page, limit, includeUnpaidCancelled });
        } catch (error) {
            const message = (error.response?.data?.message) || error.message || error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

export const getAdminOrderSummary = createAsyncThunk(
    'order/getSummary',
    async (_, thunkAPI) => {
        try {
            return await orderService.getAdminOrderSummary();
        } catch (error) {
            const message = (error.response?.data?.message) || error.message || error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

export const getAdminOrderById = createAsyncThunk(
    'order/getAdminOrder',
    async (orderId, thunkAPI) => {
        try {
            return await orderService.getAdminOrderById(orderId);
        } catch (error) {
            const message = (error.response?.data?.message) || error.message || error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

export const updateOrderStatus = createAsyncThunk(
    'order/updateStatus',
    async ({ orderId, status }, thunkAPI) => {
        try {
            return await orderService.updateOrderStatus(orderId, status);
        } catch (error) {
            const message = (error.response?.data?.message) || error.message || error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

export const getOmnivaLocations = createAsyncThunk(
    'order/getOmnivaLocations',
    async (_, thunkAPI) => {
        try {
            return await orderService.getOmnivaLocations();
        } catch (error) {
            const message = (error.response?.data?.message) || error.message || error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

export const orderSlice = createSlice({
    name: 'order',
    initialState,
    reducers: {
        reset: (state) => {
            state.isLoading = false;
            state.isSuccess = false;
            state.isError = false;
            state.isLocationsLoading = false;
            state.isSummaryLoading = false;
            state.message = '';
            state.summaryMessage = '';
            state.orders = [];
            state.currentPage = 1;
            state.totalPages = 1;
            state.totalOrders = 0;
            state.pageLimit = 20;
        },
        clearOrder: (state) => {
            state.order = null;
            state.isError = false;
            state.isSuccess = false;
            state.isLoading = false;
            state.isLocationsLoading = false;
            state.message = '';
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(initiateStripe.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(initiateStripe.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
            })
            .addCase(initiateStripe.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
            })
            .addCase(getOrderBySessionId.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(getOrderBySessionId.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.order = action.payload;
            })
            .addCase(getOrderBySessionId.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
            })
            .addCase(getAdminOrders.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(getAdminOrders.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.orders = action.payload.orders;
                state.currentPage = action.payload.page;
                state.totalPages = action.payload.pages;
                state.totalOrders = action.payload.total;
                state.pageLimit = action.payload.limit;
            })
            .addCase(getAdminOrders.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
            })
            .addCase(getAdminOrderSummary.pending, (state) => {
                state.isSummaryLoading = true;
                state.summaryMessage = '';
            })
            .addCase(getAdminOrderSummary.fulfilled, (state, action) => {
                state.isSummaryLoading = false;
                state.summary = action.payload;
            })
            .addCase(getAdminOrderSummary.rejected, (state, action) => {
                state.isSummaryLoading = false;
                state.summaryMessage = action.payload;
            })
            .addCase(getAdminOrderById.pending, (state) => {
                state.isLoading = true;
                state.isError = false;
                state.message = '';
            })
            .addCase(getAdminOrderById.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.order = action.payload;
            })
            .addCase(getAdminOrderById.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
            })
            .addCase(updateOrderStatus.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                // Update the specific order in the orders array
                const index = state.orders.findIndex(order => order._id === action.payload._id);
                if (index !== -1) {
                    state.orders[index] = action.payload;
                }
                if (state.order && state.order._id === action.payload._id) {
                    state.order = action.payload;
                }
            })
            .addCase(getOmnivaLocations.pending, (state) => {
                state.isLocationsLoading = true;
                state.isError = false;
            })
            .addCase(getOmnivaLocations.fulfilled, (state, action) => {
                state.isLocationsLoading = false;
                state.isSuccess = true;
                state.omniva_locations = action.payload;
            })
            .addCase(getOmnivaLocations.rejected, (state, action) => {
                state.isLocationsLoading = false;
                state.isError = true;
                state.message = action.payload;
            })
    },
});

export const { reset, clearOrder } = orderSlice.actions;
export default orderSlice.reducer;
